import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Input,
  Button,
  Text,
  Badge,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  TableContainer,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { io } from 'socket.io-client';
import axios from 'axios';
import { BASE_URL } from '../../../config/api';
import { usePermissions } from 'hooks/usePermissions';

const getSocketServerUrl = () => {
  try {
    return new URL(BASE_URL).origin;
  } catch (_) {
    return BASE_URL;
  }
};

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [filters, setFilters] = useState({
    action: '',
    user_id: '',
    resource: '',
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 50,
    hasNext: false,
    hasPrev: false
  });
  const toast = useToast();

  // Fonction pour formater les noms de champs
  const formatFieldName = (field) => {
    const fieldLabels = {
      patient_id: 'Patient ID',
      etat_mission: 'État Mission',
      statut: 'Statut',
      date_mission: 'Date Mission',
      heure_depart: 'Heure Départ',
      temps_depart: 'Temps Départ',
      heure_arrivee: 'Heure Arrivée',
      temps_arrivee: 'Temps Arrivée',
      heure_affectation: 'Heure Affectation',
      temps_total: 'Temps Total',
      heure_redepart: 'Heure Redépart',
      temps_redepart: 'Temps Redépart',
      heure_fin: 'Heure Fin',
      temps_fin: 'Temps Fin',
      urgence: 'Urgence',
      type_mission: 'Type Mission',
      cause: 'Cause',
      demande_materiel: 'Demande Matériel',
      ambulancier_id: 'Ambulancier ID',
      ambulance_id: 'Ambulance ID',
      type_ambulance: 'Type Ambulance',
      adresse: 'Adresse',
      adresse_destination: 'Adresse Destination',
      phone: 'Téléphone',
      doctor_id: 'Médecin ID',
      nurse_id: 'Infirmier ID',
      nurse_type: 'Type Infirmier',
      ville: 'Ville',
      perimetre: 'Périmètre',
      loc: 'Localisation',
      autoroute: 'Autoroute',
      piece_jointe: 'Pièce Jointe',
      timer: 'Timer',
      // Ajouter d'autres champs selon les besoins
    };
    return fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  useEffect(() => {
    fetchLogs();
    const cleanupSocket = setupSocket();

    return () => {
      if (typeof cleanupSocket === 'function') {
        cleanupSocket();
      }
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      applyFilters();
    }, 500); // Debounce filter changes

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const fetchLogs = async (page = 1, customLimit = null) => {
    setLoading(true);
    try {
      const currentLimit = customLimit || pagination.limit;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: currentLimit.toString(),
        ...filters
      });

      // Remove empty filters
      Object.keys(filters).forEach(key => {
        if (!filters[key]) {
          params.delete(key);
        }
      });

      const response = await axios.get(`${BASE_URL}/logs?${params}`, {
        withCredentials: true,
      });

      setLogs(response.data.logs || []);
      const newPagination = {
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1,
        currentPage: response.data.pagination?.currentPage || 1,
        limit: customLimit || response.data.pagination?.limit || pagination.limit,
        hasNext: response.data.pagination?.hasNext || false,
        hasPrev: response.data.pagination?.hasPrev || false
      };
      console.log('Pagination update:', { customLimit, responsePagination: response.data.pagination, newPagination });
      setPagination(newPagination);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les logs',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const setupSocket = () => {
    const socket = io(getSocketServerUrl(), {
      transports: ['websocket'],
      withCredentials: true,
    });

    socket.on('connect', () => {
      console.log('Socket connected for logs');
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected for logs');
    });

    socket.on('new_log', (newLog) => {
      console.log('Received new_log:', newLog);
      // Refresh current page to include new log
      fetchLogs();
    });

    return () => {
      socket.disconnect();
    };
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const applyFilters = () => {
    fetchLogs(1); // Reset to first page when applying filters
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      user_id: '',
      resource: '',
    });
    fetchLogs(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchLogs(newPage);
    }
  };

  const handleLimitChange = (newLimit) => {
    const limit = parseInt(newLimit);
    console.log('handleLimitChange called:', { newLimit, limit, currentLimit: pagination.limit });
    if (limit > 0 && limit !== pagination.limit) {
      // Update pagination state immediately
      setPagination(prev => ({ ...prev, limit }));
      // Fetch with new limit
      fetchLogs(1, limit);
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'CREATE':
        return 'green';
      case 'UPDATE':
        return 'blue';
      case 'DELETE':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (permissionsLoading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  if (!hasPermission('logs_view')) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Flex justify="center" align="center" h="400px">
          <Text color="red.500" fontSize="lg">Accès non autorisé — permission <strong>logs_view</strong> requise.</Text>
        </Flex>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} p={6}>
      <Flex direction="column" gap={6}>
        {/* Header */}
        <Box>
          <Text fontSize="2xl" fontWeight="bold" mb={2}>
            Logs d'Audit
          </Text>
          <Text color="gray.600">
            Consultez l'historique des actions effectuées dans le système
          </Text>
        </Box>

        {/* Filtres */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <Text fontSize="lg" fontWeight="semibold" mb={2}>
            Filtres
          </Text>
          <Text fontSize="sm" color="gray.600" mb={4}>
            La recherche par nom et ressource est insensible à la casse
          </Text>
          <Flex gap={4} wrap="wrap" align="center">
            <Select
              placeholder="Filtrer par action"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              w="200px"
            >
              <option value="CREATE">CREATE</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </Select>

            <Input
              placeholder="Nom ou ID utilisateur..."
              value={filters.user_id}
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
              w="200px"
            />

            <Input
              placeholder="Rechercher ressource..."
              value={filters.resource}
              onChange={(e) => handleFilterChange('resource', e.target.value)}
              w="200px"
            />

            <Button onClick={clearFilters} colorScheme="gray" variant="outline">
              Effacer les filtres
            </Button>
          </Flex>
        </Box>

        {/* Table des logs */}
        <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
          <Box p={6} borderBottom="1px" borderColor="gray.200">
            <Text fontSize="lg" fontWeight="semibold">
              Historique des logs ({pagination.total} total)
            </Text>
          </Box>
          
          <TableContainer border="1px solid" borderColor="gray.200" borderRadius="12px" overflowX="auto">
            <Table variant="simple" sx={{ 'th, td': { borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'gray.200', whiteSpace: 'nowrap' }, 'th:last-child, td:last-child': { borderRight: 'none' }, 'tr:last-child td': { borderBottom: 'none' } }}>
              <Thead>
                <Tr bg="gray.50">
                  <Th>Date</Th>
                  <Th>Utilisateur</Th>
                  <Th>Action</Th>
                  <Th>Ressource</Th>
                  <Th>ID Ressource</Th>
                  <Th>Détails</Th>
                </Tr>
              </Thead>
              <Tbody>
                {logs.map((log) => (
                  <Tr key={log.id} _hover={{ bg: "gray.50" }}>
                    <Td>
                      <Text fontSize="sm">
                        {new Date(log.time).toLocaleString('fr-FR')}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontWeight="medium">
                        {log.user_name || (log.user_id ? `ID: ${log.user_id}` : 'Système')}
                      </Text>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={getActionBadgeColor(log.action)}
                        variant="subtle"
                        px={2}
                        py={1}
                      >
                        {log.action}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {log.resource}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" fontFamily="mono">
                        {log.resource_id || '-'}
                      </Text>
                    </Td>
                    <Td>
                      {log.meta && (log.meta.changedFields || log.meta.fields) ? (
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                            {log.action === 'CREATE' ? 'Champs créés:' : 'Champs modifiés:'}
                          </Text>
                          <Flex flexWrap="wrap" gap={1}>
                            {(log.meta.changedFields || log.meta.fields).map((field, index) => (
                              <Badge
                                key={index}
                                size="sm"
                                colorScheme={log.action === 'CREATE' ? 'green' : 'blue'}
                                variant="subtle"
                                fontSize="xs"
                              >
                                {formatFieldName(field)}
                              </Badge>
                            ))}
                          </Flex>
                        </Box>
                      ) : (
                        <Text fontSize="sm" color="gray.500">-</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Pagination Controls - Always visible when there are logs */}
          <Box p={4} borderTop="1px" borderColor="gray.200" bg="gray.50">
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  Page {pagination.currentPage} sur {pagination.totalPages}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  ({pagination.total} logs total)
                </Text>
              </HStack>

              <HStack spacing={4}>
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">Éléments par page:</Text>
                  <Select
                    size="sm"
                    value={pagination.limit}
                    onChange={(e) => handleLimitChange(e.target.value)}
                    w="80px"
                    bg="white"
                    borderColor="gray.300"
                    _hover={{ borderColor: "blue.300" }}
                    _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px blue.400" }}
                    isDisabled={paginationLoading}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </Select>
                </HStack>

                <HStack spacing={1}>
                  <IconButton
                    icon={<ChevronLeftIcon />}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    isDisabled={!pagination.hasPrev || paginationLoading}
                    isLoading={paginationLoading}
                    size="sm"
                    aria-label="Page précédente"
                    variant="outline"
                    colorScheme="blue"
                    bg="white"
                    _hover={{ bg: "blue.50" }}
                  />
                  <Box
                    px={3}
                    py={1}
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.300"
                    minW="60px"
                    textAlign="center"
                  >
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      {paginationLoading ? '...' : `${pagination.currentPage} / ${pagination.totalPages}`}
                    </Text>
                  </Box>
                  <IconButton
                    icon={<ChevronRightIcon />}
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    isDisabled={!pagination.hasNext || paginationLoading}
                    isLoading={paginationLoading}
                    size="sm"
                    aria-label="Page suivante"
                    variant="outline"
                    colorScheme="blue"
                    bg="white"
                    _hover={{ bg: "blue.50" }}
                  />
                </HStack>
              </HStack>
            </Flex>
          </Box>
        </Box>

        {logs.length === 0 && !loading && (
          <Alert status="info" borderRadius="lg">
            <AlertIcon />
            Aucun log trouvé avec les filtres actuels.
          </Alert>
        )}
      </Flex>
    </Box>
  );
};

export default Logs;